/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/AppointmentRequestSections/CommercialOffer/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import styles from "./CommercialOffer.module.css";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  getExportsOptions,
  getImportsOptions,
  getSellsToGovernmentOptions,
  getCountryExportingToOptions,
  getCountryImportingFromOptions,
} from "../../../data/selectOptions";
import { getSelectedOption, selectStyles } from "../../../utils/common";

const CommercialOffer = ({ company, getFieldValidation }) => {
  const { t } = useTranslation();

  const exportsOptions = getExportsOptions(t);
  const importsOptions = getImportsOptions(t);
  const sellsToGovernmentOptions = getSellsToGovernmentOptions(t);
  const countryExportingToOptions = getCountryExportingToOptions(t);
  const countryImportingFromOptions = getCountryImportingFromOptions(t);

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.commercialOffer.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.exportsLabel")}
          </label>
          <Select
            value={getSelectedOption(exportsOptions, company?.exporta)}
            options={exportsOptions}
            placeholder={t("components.commercialOffer.exportsPlaceholder")}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("exporta")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.countryExportingToLabel")}
          </label>
          <Select
            value={getSelectedOption(
              countryExportingToOptions,
              company?.paisExportacion,
            )}
            options={countryExportingToOptions}
            placeholder={t(
              "components.commercialOffer.countryExportingToPlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("paisExportacion")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
          />
        </div>
      </div>
      <div className={`${styles.inputRow} ${styles.specialSpacing}`}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.importsLabel")}
          </label>
          <Select
            value={getSelectedOption(importsOptions, company?.importa)}
            options={importsOptions}
            placeholder={t("components.commercialOffer.importsPlaceholder")}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("importa")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.countryImportingFromLabel")}
          </label>
          <Select
            value={getSelectedOption(
              countryImportingFromOptions,
              company?.paisImportacion,
            )}
            options={countryImportingFromOptions}
            placeholder={t(
              "components.commercialOffer.countryImportingFromPlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("paisImportacion")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.sellsToGovernmentLabel")}
          </label>
          <Select
            value={getSelectedOption(
              sellsToGovernmentOptions,
              company?.vendeAGobierno,
            )}
            options={sellsToGovernmentOptions}
            placeholder={t(
              "components.commercialOffer.sellsToGovernmentPlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("vendeAGobierno")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
          />
        </div>
      </div>
    </div>
  );
};

export default CommercialOffer;
